import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/common';
import { useResource } from '@/hooks';
import { ApiRequestError, errorMessage } from '@/lib/api-client';
import { newId } from '@/lib/id';
import { type OutboxItem, outbox } from '@/lib/outbox';
import type { Entry, EntryKind, EntryPatch, EntrySource, NewEntry, User } from '@/types/api';
import type { Code, Material, Surface } from '@/utils/dental';
import { entryKindFor } from '@/utils/entryKind';
import { chunk, failureKind } from '../../sync/syncOutbox';
import { entriesApi } from '../api';

export type DraftLike = {
  tooth: number;
  code: Code;
  surfaces: Surface[];
  material: Material | null;
  note: string | null;
  transcript?: string;
  confidence?: number;
  /** Existing state or performed work; findings are always existing. */
  kind?: EntryKind;
};

function toLocal(item: OutboxItem): Entry {
  const e = item.entry;
  return {
    id: `local-${e.clientId}`,
    clientId: e.clientId,
    patientId: item.patientId,
    visitId: item.visitId ?? null,
    tooth: e.tooth,
    surfaces: e.surfaces,
    code: e.code,
    material: e.material ?? null,
    note: e.note ?? null,
    source: e.source,
    status: 'APPROVED',
    kind: entryKindFor(e.code, e.kind),
    transcript: e.transcript ?? null,
    transcriptId: null,
    confidence: e.confidence ?? null,
    corrected: false,
    rejectReason: null,
    createdBy: { id: item.authorId, name: item.authorName },
    createdAt: e.recordedAt ?? item.queuedAt,
    approvedBy: null,
    approvedAt: null,
    local: true,
  };
}

export function useChartEntries(patientId: string, user: User | null, dentistId: string | null) {
  const { t } = useTranslation();
  const toast = useToast();
  const server = useResource(() => entriesApi.list(patientId), patientId);
  const [queued, setQueued] = useState<OutboxItem[]>([]);
  const [inflight, setInflight] = useState<OutboxItem[]>([]);
  const [recent, setRecent] = useState<Map<number, number>>(new Map());
  const { reload } = server;
  const userId = user?.id ?? null;

  useEffect(() => {
    let alive = true;
    let previous = -1;
    const load = () =>
      outbox.forPatient(patientId).then((all) => {
        if (!alive) return;
        // Entries another user queued on this device stay hidden until they sign in.
        const items = all.filter((i) => i.authorId === userId);
        setQueued(items);
        // Something left the queue: it reached the server.
        if (previous > items.length) void reload();
        previous = items.length;
      });
    void load();
    const off = outbox.subscribe(() => void load());
    return () => {
      alive = false;
      off();
    };
  }, [patientId, userId, reload]);

  // Recent highlights fade after 8 s.
  useEffect(() => {
    if (!recent.size) return;
    const t = setTimeout(() => {
      const now = Date.now();
      setRecent((m) => new Map([...m].filter(([, at]) => now - at < 8000)));
    }, 8000);
    return () => clearTimeout(t);
  }, [recent]);

  const markRecent = useCallback((teeth: number[]) => {
    setRecent((m) => {
      const next = new Map(m);
      for (const t of teeth) next.set(t, Date.now());
      return next;
    });
  }, []);

  const entries = useMemo(() => {
    const list = [...(server.data ?? [])];
    const seen = new Set(list.map((e) => e.clientId));
    for (const item of [...queued, ...inflight]) {
      if (!seen.has(item.clientId)) {
        seen.add(item.clientId);
        list.push(toLocal(item));
      }
    }
    return list;
  }, [server.data, queued, inflight]);

  const replace = useCallback(
    (updated: Entry) =>
      server.setData((list) => list?.map((e) => (e.id === updated.id ? updated : e))),
    [server]
  );

  /** Send in batches of 50; on a temporary failure queue the rest on the device. */
  const sendOrQueue = useCallback(
    async (items: OutboxItem[], visitId: string | null): Promise<Entry[]> => {
      const created: Entry[] = [];
      const batches = chunk(items);
      for (let k = 0; k < batches.length; k += 1) {
        try {
          const batch = batches[k];
          created.push(
            ...(await entriesApi.create(
              patientId,
              batch.map((i) => i.entry),
              dentistId,
              visitId
            ))
          );
        } catch (err) {
          if (failureKind(err) === 'reject') {
            toast.show(errorMessage(err), 'error');
            break;
          }
          const rest = batches.slice(k).flat();
          await outbox.add(rest);
          toast.show(t('chart.toast.queued'));
          created.push(...rest.map(toLocal));
          break;
        }
      }
      const fromServer = created.filter((e) => !e.local);
      if (fromServer.length) server.setData((list) => [...(list ?? []), ...fromServer]);
      return created;
    },
    [patientId, dentistId, server, toast, t]
  );

  /**
   * `transcriptClientId`: the dictation the drafts came from (or were assigned
   * from). `visitId`: add to that visit (one already confirmed) instead of the
   * open visit of the day.
   */
  const save = useCallback(
    async (
      drafts: DraftLike[],
      source: EntrySource,
      transcriptClientId?: string,
      visitId: string | null = null
    ): Promise<Entry[]> => {
      if (!(user && drafts.length)) return [];
      const now = new Date().toISOString();
      const items: OutboxItem[] = drafts.map((d) => {
        const entry: NewEntry = {
          clientId: newId(),
          tooth: d.tooth,
          surfaces: d.surfaces,
          code: d.code,
          material: d.material,
          note: d.note,
          source,
          kind: entryKindFor(d.code, d.kind),
          transcript: transcriptClientId ? (d.transcript ?? null) : null,
          transcriptClientId,
          confidence: source === 'DICTATION' ? (d.confidence ?? null) : null,
          recordedAt: now,
        };
        return {
          clientId: entry.clientId,
          patientId,
          dentistId,
          visitId,
          entry,
          authorId: user.id,
          authorName: user.name,
          queuedAt: now,
        };
      });
      setInflight((list) => [...list, ...items]);
      markRecent(drafts.map((d) => d.tooth));
      const ids = new Set(items.map((i) => i.clientId));
      try {
        return await sendOrQueue(items, visitId);
      } finally {
        setInflight((list) => list.filter((i) => !ids.has(i.clientId)));
      }
    },
    [user, patientId, dentistId, markRecent, sendOrQueue]
  );

  const run = useCallback(
    async (action: () => Promise<Entry>) => {
      try {
        const updated = await action();
        replace(updated);
        markRecent([updated.tooth]);
        return updated;
      } catch (err) {
        toast.show(
          err instanceof ApiRequestError && err.isNetwork
            ? t('chart.toast.needsOnline')
            : errorMessage(err),
          'error'
        );
        return null;
      }
    },
    [replace, markRecent, toast, t]
  );

  const reject = useCallback(
    (e: Entry, reason?: string) => run(() => entriesApi.reject(e.id, reason)),
    [run]
  );
  const correct = useCallback(
    (e: Entry, patch: EntryPatch) => run(() => entriesApi.correct(e.id, patch)),
    [run]
  );
  return {
    entries,
    loading: server.loading && !server.data,
    error: server.error,
    reload,
    recent: useMemo(() => new Set(recent.keys()), [recent]),
    save,
    reject,
    correct,
  };
}
